import React, { useRef, useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Eraser, Undo2, RotateCcw, Check, Loader2, Eye, EyeOff } from "lucide-react";

/**
 * HealingTool - A canvas-based painting tool for marking areas to heal
 * 
 * Users paint red over areas they want to heal (like face shine), and the
 * backend uses inpainting to blend those areas with surrounding pixels.
 */
export default function HealingTool({ 
  imageUrl, 
  onHeal, 
  disabled = false,
  className = "" 
}) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(15);
  const [history, setHistory] = useState([]);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Before/after preview state
  const [showBeforeAfter, setShowBeforeAfter] = useState(false);
  const [beforeImageUrl, setBeforeImageUrl] = useState(null);
  const [afterImageUrl, setAfterImageUrl] = useState(null);
  
  // Store original image URL for before comparison
  useEffect(() => {
    if (imageUrl && !beforeImageUrl) {
      setBeforeImageUrl(imageUrl);
    }
  }, [imageUrl, beforeImageUrl]);
  
  // Load image and set up canvas
  useEffect(() => {
    if (!imageUrl || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    
    img.onload = () => {
      // Set canvas size to match image
      canvas.width = img.width;
      canvas.height = img.height;
      setImageDimensions({ width: img.width, height: img.height });
      
      // Clear canvas (transparent)
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      setImageLoaded(true);
      setHistory([]);
    };
    
    img.onerror = () => {
      console.error("Failed to load image");
      setImageLoaded(false);
    };
    
    img.src = imageUrl;
  }, [imageUrl]);
  
  // Save current state to history
  const saveToHistory = useCallback(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const imageData = canvas.getContext("2d").getImageData(0, 0, canvas.width, canvas.height);
    setHistory(prev => [...prev.slice(-9), imageData]); // Keep last 10 states
  }, []);
  
  // Get mouse position relative to canvas
  const getMousePos = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  }, []);
  
  // Draw on canvas
  const draw = useCallback((e) => {
    if (!isDrawing || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getMousePos(e);
    
    // Draw red circle (healing indicator)
    ctx.fillStyle = "rgba(255, 50, 50, 0.7)";
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, brushSize, 0, Math.PI * 2);
    ctx.fill();
  }, [isDrawing, brushSize, getMousePos]);
  
  // Handle touch events
  const getTouchPos = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas || !e.touches[0]) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (e.touches[0].clientX - rect.left) * scaleX,
      y: (e.touches[0].clientY - rect.top) * scaleY
    };
  }, []);
  
  const drawTouch = useCallback((e) => {
    if (!isDrawing || !canvasRef.current) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getTouchPos(e);
    
    ctx.fillStyle = "rgba(255, 50, 50, 0.7)";
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, brushSize, 0, Math.PI * 2);
    ctx.fill();
  }, [isDrawing, brushSize, getTouchPos]);
  
  // Mouse/touch handlers
  const handleStart = (e) => {
    saveToHistory();
    setIsDrawing(true);
    if (e.type === "mousedown") {
      draw(e);
    } else {
      drawTouch(e);
    }
  };
  
  const handleMove = (e) => {
    if (e.type === "mousemove") {
      draw(e);
    } else {
      drawTouch(e);
    }
  };
  
  const handleEnd = () => {
    setIsDrawing(false);
  };
  
  // Undo last stroke
  const handleUndo = () => {
    if (history.length === 0 || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const lastState = history[history.length - 1];
    
    ctx.putImageData(lastState, 0, 0);
    setHistory(prev => prev.slice(0, -1));
  };
  
  // Clear all marks
  const handleClear = () => {
    if (!canvasRef.current) return;
    
    saveToHistory();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };
  
  // Get mask as base64 data URL
  const getMaskDataUrl = () => {
    if (!canvasRef.current) return null;
    return canvasRef.current.toDataURL("image/png");
  };
  
  // Check if there are any marks on the canvas
  const hasMarks = () => {
    if (!canvasRef.current) return false;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    
    // Check if any pixel has non-zero alpha
    for (let i = 3; i < imageData.data.length; i += 4) {
      if (imageData.data[i] > 0) return true;
    }
    return false;
  };
  
  // Apply healing
  const handleApply = async () => {
    if (!hasMarks() || !onHeal) return;
    
    setIsProcessing(true);
    try {
      const maskDataUrl = getMaskDataUrl();
      const newImageUrl = await onHeal(maskDataUrl);
      
      // Store after image for comparison
      if (newImageUrl) {
        setAfterImageUrl(newImageUrl);
      }
      
      // Clear canvas after successful heal
      handleClear();
    } catch (error) {
      console.error("Healing failed:", error);
    } finally {
      setIsProcessing(false);
    }
  };
  
  if (!imageUrl) {
    return null;
  }
  
  return (
    <div className={`space-y-3 ${className}`} data-testid="healing-tool">
      {/* Before/After Toggle */}
      {beforeImageUrl && afterImageUrl && (
        <div className="flex items-center justify-between rounded-xl bg-white/5 px-3 py-2">
          <span className="text-sm text-zinc-300">Compare Before/After</span>
          <Button
            onClick={() => setShowBeforeAfter(!showBeforeAfter)}
            variant="ghost"
            size="sm"
            className={`rounded-full ${showBeforeAfter ? 'bg-violet-500/20 text-violet-300' : 'bg-white/5 text-zinc-400'}`}
            data-testid="toggle-before-after"
          >
            {showBeforeAfter ? <EyeOff className="mr-1.5 h-3.5 w-3.5" /> : <Eye className="mr-1.5 h-3.5 w-3.5" />}
            {showBeforeAfter ? "Hide" : "Show"}
          </Button>
        </div>
      )}
      
      {/* Before/After Preview */}
      {showBeforeAfter && beforeImageUrl && afterImageUrl && (
        <div className="grid grid-cols-2 gap-3 rounded-xl border border-white/10 p-3 bg-black/20">
          <div className="space-y-1">
            <div className="text-xs text-zinc-500 text-center">Before</div>
            <img 
              src={beforeImageUrl} 
              alt="Before" 
              className="w-full h-auto rounded-lg object-contain max-h-48"
            />
          </div>
          <div className="space-y-1">
            <div className="text-xs text-zinc-500 text-center">After</div>
            <img 
              src={afterImageUrl} 
              alt="After" 
              className="w-full h-auto rounded-lg object-contain max-h-48"
            />
          </div>
        </div>
      )}
      
      {/* Canvas overlay on image */}
      <div 
        className="relative overflow-hidden rounded-xl border border-white/10"
        style={{ maxHeight: "400px" }}
      >
        {/* Background image */}
        <img 
          src={imageUrl} 
          alt="Source" 
          className="w-full h-auto object-contain"
          style={{ maxHeight: "400px" }}
          data-testid="healing-source-image"
        />
        
        {/* Drawing canvas overlay */}
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full cursor-crosshair"
          style={{ 
            touchAction: "none",
            mixBlendMode: "normal"
          }}
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
          data-testid="healing-canvas"
        />
        
        {/* Processing overlay */}
        {isProcessing && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <div className="text-center space-y-2">
              <Loader2 className="h-8 w-8 animate-spin text-violet-400 mx-auto" />
              <div className="text-sm text-zinc-300">Healing...</div>
            </div>
          </div>
        )}
      </div>
      
      {/* Brush size slider */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-zinc-300">Brush Size</span>
          <span className="text-xs text-zinc-500">{brushSize}px</span>
        </div>
        <Slider
          value={[brushSize]}
          onValueChange={([v]) => setBrushSize(v)}
          min={5}
          max={50}
          step={1}
          className="w-full"
          data-testid="healing-brush-slider"
        />
      </div>
      
      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={handleUndo}
          disabled={history.length === 0 || disabled || isProcessing}
          variant="secondary"
          size="sm"
          className="rounded-full bg-white/5 text-zinc-100 ring-1 ring-white/10 hover:bg-white/10"
          data-testid="healing-undo-btn"
        >
          <Undo2 className="mr-1.5 h-3.5 w-3.5" />
          Undo
        </Button>
        
        <Button
          onClick={handleClear}
          disabled={disabled || isProcessing}
          variant="secondary"
          size="sm"
          className="rounded-full bg-white/5 text-zinc-100 ring-1 ring-white/10 hover:bg-white/10"
          data-testid="healing-clear-btn"
        >
          <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
          Clear
        </Button>
        
        <Button
          onClick={handleApply}
          disabled={!hasMarks() || disabled || isProcessing}
          size="sm"
          className="rounded-full bg-emerald-600 text-white hover:bg-emerald-500 ml-auto"
          data-testid="healing-apply-btn"
        >
          {isProcessing ? (
            <>
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              Healing...
            </>
          ) : (
            <>
              <Check className="mr-1.5 h-3.5 w-3.5" />
              Apply Healing
            </>
          )}
        </Button>
      </div>
      
      {/* Instructions */}
      <div className="text-xs text-zinc-500 space-y-1">
        <p>Paint <span className="text-red-400">red</span> over areas you want to heal (shine, blemishes).</p>
        <p>The tool will blend marked areas with surrounding pixels.</p>
      </div>
    </div>
  );
}
